import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface LocationResult {
    place_id: number;
    lat: string;
    lon: string;
    display_name: string;
    type: string;
    address?: {
        country?: string;
    };
}

@Component({
    selector: 'jhi-location-search',
    template: `
        <div class="search-container">
            <div class="input-group mb-2">
                <input
                        type="text"
                        class="form-control"
                        [(ngModel)]="searchQuery"
                        (keyup)="onSearchKeyUp()"
                        placeholder="{{'field-manager.study-instances.search.location' | translate}}"
                        [attr.aria-describedby]="'searchButton'"
                >

                    <button
                            class="btn btn-secondary"
                            type="button"
                            id="searchButton"
                            (click)="searchLocation()"
                            [disabled]="isLoading"
                    >
                        <i class="fa fa-search"></i>
                        {{ (isLoading ? 'field-manager.action.searching' : 'field-manager.action.search') | translate }}
                    </button>
            </div>

            <!-- Results Dropdown -->
            <div class="dropdown-container" *ngIf="showDropdown">
                <div class="dropdown-menu show w-100">
                    <div *ngIf="isLoading" class="dropdown-item text-center">
                        <i class="fa fa-spinner fa-spin me-2"></i> {{ 'field-manager.action.searching' | translate }}
                    </div>

                    <div *ngIf="!isLoading && results.length === 0" class="dropdown-item text-center"
                        jhiTranslate="field-manager.study-instances.no.results">
                    </div>

                    <ng-container *ngIf="!isLoading && results.length > 0">
                        <a
                                *ngFor="let result of results"
                                class="dropdown-item"
                                href="javascript:void(0)"
                                (click)="selectLocation(result)"
                        >
                            <div class="location-item">
                                <div class="location-name">{{ result.display_name }}</div>
                            </div>
                        </a>
                    </ng-container>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .search-container {
            position: relative;
            padding-left: 15px;
        }

        .dropdown-container {
            position: absolute;
            width: 100%;
            z-index: 1000;
        }

        .dropdown-menu {
            max-height: 300px;
            overflow-y: auto;
            width: 100%;
            padding: 0;
            margin-top: 0;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
        }

        .location-item {
            padding: 8px 0;
        }

        .location-name {
            font-weight: 500;
        }

        .location-details {
            font-size: 0.875rem;
            color: #6c757d;
        }

        .dropdown-item {
            border-bottom: 1px solid #dee2e6;
            padding: 0.5rem 1rem;
        }

        .dropdown-item:last-child {
            border-bottom: none;
        }

        .dropdown-item:hover {
            background-color: #f8f9fa;
        }
    `]
})
export class LocationSearchComponent implements OnDestroy {
    @Output() locationSelected = new EventEmitter<{ lat: number, lon: number, displayName: string }>();

    searchQuery = '';
    results: LocationResult[] = [];
    isLoading = false;
    showDropdown = false;
    private searchSubject = new Subject<string>();
    private searchSubscription: Subscription;

    constructor(private http: HttpClient) {
        // Setup debounced search
        this.searchSubscription = this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(() => {
            this.searchLocation();
        });
    }

    onSearchKeyUp(): void {
        this.searchSubject.next(this.searchQuery);
    }

    async searchLocation(): Promise<void> {
        if (!this.searchQuery.trim()) {
            this.results = [];
            this.showDropdown = false;
            return;
        }

        this.isLoading = true;
        this.showDropdown = true;

        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.searchQuery)}`;

        try {
            this.results = await this.http.get<LocationResult[]>(url).toPromise();
        } catch (error) {
            console.error('Error searching location:', error);
            this.results = [];
        } finally {
            this.isLoading = false;
        }
    }

    selectLocation(result: LocationResult): void {
        this.searchQuery = result.display_name;
        this.showDropdown = false;
        this.locationSelected.emit({
            lat: parseFloat(result.lat),
            lon: parseFloat(result.lon),
            displayName: result.display_name
        });
    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }
}
